import { Filter, Pagination, Search, Sort } from "@/components/Table/Table";
import { YearStatus } from "@/components/YearSelector/YearSelector";
import useSmartQueryClient from "@/hooks/useSmartQueryClient";
import { ExportResponse, filterSearchToGetFilter, GetData, sortToParam } from "@/utilities/api";
import { useMutation, useQuery, useInfiniteQuery } from "@tanstack/react-query";
import axios from "axios";
import { IImpact, ImpactMetric } from "../dashboard/utils/types";
import { Product } from "./overview/ProductsOverview";

export function useGetProducts({
  year,
  pagination,
  filter,
  search,
  sort,
  enabled,
  showUnrelated = false,
}: {
  year?: number;
  pagination?: Pagination;
  filter?: Filter;
  search?: Search;
  sort?: Sort;
  enabled?: boolean;
  showUnrelated?: boolean;
}) {
  const yearParam = year ? { year } : {};
  const showUnrelatedParam = showUnrelated ? { show_unrelated: showUnrelated } : {};

  return useQuery({
    queryKey: ["products", "list", year, showUnrelated, pagination, filter, search, sort],
    keepPreviousData: true,
    enabled: enabled ?? true,
    queryFn: () =>
      axios
        .get<GetData<Product>>("/products", {
          params: {
            ...yearParam,
            ...showUnrelatedParam,
            page: pagination?.page === undefined ? 1 : pagination.page,
            page_size: pagination?.pageSize || 50,
            ...filterSearchToGetFilter(filter, search),
            ...sortToParam(sort),
          },
        })
        .then((result) => result.data),
  });
}

export interface SearchProduct {
  id: number;
  label: string;
}

export function useGetProductsSearch({
  search,
  selectedIds,
}: {
  search?: string;
  selectedIds?: number[];
}) {
  const searchParam = search ? { search } : {};
  const selectedIdsParam = selectedIds ? { selected_ids: selectedIds.join(";") } : {};

  return useInfiniteQuery({
    queryKey: ["products", "search", search || "", selectedIds?.sort().join(",") || ""],
    queryFn: ({ pageParam = 1 }) =>
      axios.get<GetData<SearchProduct>>("/products/search", {
        params: {
          ...searchParam,
          ...selectedIdsParam,
          page: pageParam,
          page_size: 50,
        },
      }),
    getNextPageParam: (lastPage, allPages) => {
      const totalItems = lastPage.data.total_count || 0;
      const itemsLoaded = allPages.reduce((sum, page) => sum + page.data.data.length, 0);
      const hasMore = itemsLoaded < totalItems;
      return hasMore ? allPages.length + 1 : undefined;
    },
    keepPreviousData: true,
  });
}

export function useGetProductsBom({
  year,
  pagination,
  filter,
  search,
  sort,
  enabled,
  showUnrelated = false,
}: {
  year?: number;
  pagination?: Pagination;
  filter?: Filter;
  search?: Search;
  sort?: Sort;
  enabled?: boolean;
  showUnrelated?: boolean;
}) {
  const yearParam = year ? { year } : {};
  const showUnrelatedParam = showUnrelated ? { show_unrelated: showUnrelated } : {};

  return useQuery({
    queryKey: ["products", "list", "bom", year, showUnrelated, pagination, filter, search, sort],
    enabled: enabled ?? true,
    queryFn: () =>
      axios
        .get<GetData<Product>>("/products/bom", {
          params: {
            ...yearParam,
            ...showUnrelatedParam,
            page: pagination?.page === undefined ? 1 : pagination.page,
            page_size: pagination?.pageSize || 50,
            ...filterSearchToGetFilter(filter, search),
            ...sortToParam(sort),
          },
        })
        .then((result) => result.data),
  });
}

export interface BomItem {
  id: number;
  proxy_id: number;
  proxy_weight: number;
  level: number;
  parent_id: number;
  external_id: string;
  name: string;
  quantity: number;
  unit: string;
  total_quantity: number;
  total_mass: number;
  supplier_name: string;
  supplier_address: string;
  children: BomItem[];
  status: "complete" | "incomplete";
  impact: IImpact;
}

export interface BomProduct {
  id: number;
  proxy_id: number;
  proxy_weight: number;
  external_id: string;
  name: string;
  is_out_of_scope: boolean;
}

export interface RelationProduct {
  id: number;
  proxy_weight: number;
  external_id: string;
  name: string;
  relation: ("proxy" | "primary_source" | "unrelated" | "related")[];
}

export interface GetProduct {
  product: Product;
}

export interface GetProductBOM {
  product: Product;
  total_count: number;
  complete_count?: number;
  incomplete_count?: number;
  out_of_scope_count?: number;
  bom: BomItem;
}

export function useGetProduct({ productId, enabled }: { productId: number; enabled?: boolean }) {
  return useQuery({
    queryKey: ["products", "detail", productId, "details"],
    enabled,
    queryFn: () => axios.get<GetProduct>("/products/" + productId).then((result) => result.data),
  });
}

export function useGetProductBOM({ productId }: { productId: number }) {
  return useQuery({
    queryKey: ["products", "detail", productId, "bom"],
    queryFn: () =>
      axios.get<GetProductBOM>("/products/" + productId + "/bom").then((result) => result.data),
  });
}

export function useGetProductRelations({ productId }: { productId: number }) {
  return useQuery({
    queryKey: ["products", "detail", productId, "relations"],
    queryFn: () =>
      axios
        .get<RelationProduct[]>("/products/" + productId + "/relations")
        .then((result) => result.data),
  });
}

export interface ProductOrderQuantity {
  year: number;
  po: number;
  so: number;
  total: number;
}

export function useGetProductOrderQuantities({ productId }: { productId: number }) {
  return useQuery({
    queryKey: ["products", "detail", productId, "order_quantities"],
    queryFn: () =>
      axios
        .get<ProductOrderQuantity[]>("/products/" + productId + "/order_quantities")
        .then((result) => result.data),
  });
}

export interface IProductScenario {
  id: number;
  name: string;
  bom: BomItem;
  impact: IImpact;
  status: "complete" | "incomplete";
  isStale?: boolean;
}

export interface GetProductScenarios {
  product: Product;
  scenarios: IProductScenario[];
}

export function useGetProductScenarios({ productId }: { productId: number }) {
  return useQuery({
    queryKey: ["products", "detail", productId, "scenarios"],
    queryFn: () =>
      axios
        .get<GetProductScenarios>("/products/" + productId + "/scenarios")
        .then((result) => result.data),
  });
}

export interface BomItemCompare extends BomItem {
  children: BomItemCompare[];
  changed: boolean;
}

export interface ICompareProductScenario {
  id: number;
  name: string;
  bom: BomItemCompare;
  impact: IImpact;
}

export interface GetCompareProductScenarios {
  product: Product;
  scenario: ICompareProductScenario;
}

export function useCompareProductScenarios({
  productId,
  scenarioId,
  compareScenarioId,
  enabled,
}: {
  productId: number;
  scenarioId: number | null;
  compareScenarioId: number | null;
  enabled?: boolean;
}) {
  return useQuery({
    enabled,
    queryKey: [
      "products",
      "detail",
      productId,
      "scenarios",
      "compare",
      scenarioId,
      compareScenarioId,
    ],
    queryFn: () =>
      axios
        .get<GetCompareProductScenarios>("/products/" + productId + "/compare_scenarios", {
          params: {
            scenario_id: scenarioId || 0,
            compare_scenario_id: compareScenarioId || 0,
          },
        })
        .then((result) => result.data),
  });
}

export function useDeleteProducts() {
  const smartQueryClient = useSmartQueryClient();

  return useMutation({
    mutationFn: ({ productIds }: { productIds: number[] }) =>
      axios.delete("/products", {
        params: {
          ids: productIds.join(";"),
        },
      }),
    onSuccess: () => {
      smartQueryClient.invalidateQueries(["products", "list"]);
    },
  });
}

export function useDeleteBom() {
  const smartQueryClient = useSmartQueryClient();

  return useMutation({
    mutationFn: ({ productId }: { productId: number }) =>
      axios.delete("/products/" + productId + "/bom"),
    onSuccess: (_, { productId }) => {
      smartQueryClient.invalidateQueries(["products", "detail", productId]);
    },
  });
}

export interface ProductPatch {
  name?: string;
  external_id?: string;
  proxy_weight?: number;
  proxy_id?: number;
  is_out_of_scope?: boolean;
  mass?: number | null;
  category?: string | null;
  has_packaging?: boolean;
  packaging_ids?: number[];
  clear?: ("proxy_id" | "proxy_weight" | "mass" | "category_id")[];
}

export function usePatchProducts() {
  const smartQueryClient = useSmartQueryClient();

  return useMutation({
    mutationFn: ({ productIds, patch }: { productIds: number[]; patch: ProductPatch }) =>
      axios.patch("/products", patch, {
        params: {
          ids: productIds.join(";"),
        },
      }),
    onSuccess: (_, { patch }) => {
      smartQueryClient.invalidateQueries(["products"]);
      smartQueryClient.invalidateQueries(["usage"]);
      smartQueryClient.invalidateQueries(["packagings"]);
    },
  });
}

export function useDeleteProxy() {
  const smartQueryClient = useSmartQueryClient();

  return useMutation({
    mutationFn: ({ productId }: { productId: number }) =>
      axios.patch("/products/" + productId, { clear: ["proxy_weight", "proxy_id"] }),
    onSuccess: (_, { productId }) => {
      smartQueryClient.invalidateQueries(["products"]);
    },
  });
}

export function useBulkDeleteProxy() {
  const smartQueryClient = useSmartQueryClient();

  return useMutation({
    mutationFn: ({ productIds }: { productIds: number[] }) =>
      axios.patch(
        "/products",
        { clear: ["proxy_weight", "proxy_id"] },
        {
          params: {
            ids: productIds.join(";"),
          },
        }
      ),
    onSuccess: (_, { productIds }) => {
      smartQueryClient.invalidateQueries(["products"]);
      smartQueryClient.invalidateQueries(["usage"]);
    },
  });
}

export interface AddScenario {
  name: string;
}

export function useAddProductScenario() {
  const smartQueryClient = useSmartQueryClient();

  return useMutation({
    mutationFn: ({ productId, scenario }: { productId: number; scenario: AddScenario }) =>
      axios.post<{ scenario: IProductScenario }>("/products/" + productId + "/scenarios", scenario),
    onSuccess: (result, { productId }) => {
      smartQueryClient.setQueryData<GetProductScenarios>(
        ["products", "detail", productId, "scenarios"],
        (getScenarios) => {
          if (!getScenarios) return undefined;
          return {
            ...getScenarios,
            scenarios: [...getScenarios.scenarios, result.data.scenario],
          };
        }
      );
    },
  });
}

export function useDeleteProductScenario() {
  const smartQueryClient = useSmartQueryClient();

  return useMutation({
    mutationFn: ({ productId, scenarioId }: { productId: number; scenarioId: number }) =>
      axios.delete("/products/" + productId + "/scenarios/" + scenarioId),
    onSuccess: (_, { productId, scenarioId }) => {
      smartQueryClient.setQueryData<GetProductScenarios>(
        ["products", "detail", productId, "scenarios"],
        (scenario) => {
          if (!scenario) return undefined;
          return { ...scenario, scenarios: scenario.scenarios.filter((x) => x.id !== scenarioId) };
        }
      );
    },
  });
}

export function usePatchProduct() {
  const smartQueryClient = useSmartQueryClient();

  return useMutation({
    mutationFn: ({ productId, patch }: { productId: number; patch: ProductPatch }) =>
      axios.patch("/products/" + productId, patch),
    onSuccess: (_, { productId }) => {
      smartQueryClient.invalidateQueries(["products", "list"]);
      smartQueryClient.invalidateQueries(["products", "detail", productId]);
      smartQueryClient.invalidateQueries(["packagings"]);
    },
  });
}

export function useExportProductsBom() {
  return useMutation({
    mutationKey: ["productExport"],
    mutationFn: ({ productIds, scenarioId }: { productIds?: number[]; scenarioId?: number }) =>
      axios
        .get<ExportResponse>("/products/bom/export", {
          params:
            productIds && scenarioId
              ? {
                  id: productIds.join(";"),
                  scenario_id: scenarioId,
                }
              : productIds
                ? {
                    id: productIds.join(";"),
                  }
                : {},
        })
        .then((result) => result.data),
  });
}

export function useExportProductLCA() {
  return useMutation({
    mutationKey: ["productLCAExport"],
    mutationFn: ({
      productId,
      metric,
      year,
    }: {
      productId: number;
      metric: ImpactMetric;
      year: number;
    }) =>
      axios
        .get<ExportResponse>(`/products/${productId}/lca_export`, {
          params: {
            product_id: productId,
            metric: metric.key,
            unit: metric.unit,
            start_date: `${year}-01-01`,
            end_date: `${year}-12-31`,
          },
        })
        .then((result) => result.data.url),
  });
}

export function useExportProductDetails() {
  return useMutation({
    mutationKey: ["productExportDetails"],
    mutationFn: ({ year }: { year?: number }) =>
      axios
        .get<ExportResponse>("/products/export", { params: { ...(year ? { year } : {}) } })
        .then((result) => result.data),
  });
}

export function useValidateProductTemplate() {
  return useMutation({
    mutationFn: (fileName: string) =>
      axios.post("/products/validate_template", {
        file_name: fileName,
      }),
  });
}

export function useValidateProductBom() {
  return useMutation({
    mutationFn: ({ fileName, productId }: { fileName: string; productId?: number }) =>
      axios.post<{ uuid: string }>(`/products${productId ? "/" + productId : ""}/validate_bom`, {
        file_name: fileName,
      }),
  });
}

export function useUploadProductBom() {
  return useMutation({
    mutationFn: ({
      fileName,
      productId,
      scenarioId,
    }: {
      fileName: string;
      productId?: number;
      scenarioId?: number;
    }) => {
      let url = `/products/upload_bom`;
      if (productId) {
        url = `/products/${productId}/upload_bom`;
      }
      if (productId && scenarioId) {
        url = `/products/${productId}/upload_scenario_bom/${scenarioId}`;
      }

      return axios.post<{ uuid: string }>(url, {
        file_name: fileName,
      });
    },
  });
}

export interface StatusUploadError {
  error_count: number;
  rows: number[];
}

export type StatusUploadStatus = "success" | "processing" | "failed" | "pending";

export interface StatusUpload {
  status: string;
  upload_status: StatusUploadStatus;
  uuid: string;
  total_rows?: number;
  complete_percentage?: number;
  remaining_rows?: number;
  complete_rows?: number;
  incomplete_rows?: number;
  summary?: Record<string, Record<string, StatusUploadError>>;
  total_bom_uploads: number;
  total_new_products: number;
}

export interface ProductYearStatus {
  year: number;
  status: "complete" | "incomplete";
  mass_status: "complete" | "incomplete";
  bom_status: "complete" | "incomplete";
  eol_status: "complete" | "incomplete";
  usage_status: "complete" | "incomplete";
}

export function useProductStatusPerYear() {
  return useQuery({
    queryKey: ["products", "years"],
    queryFn: () =>
      axios.get<ProductYearStatus[]>("/products/years", {}).then((result) => result.data),
  });
}

export function useProductUsageStatusPerYear() {
  return useQuery({
    queryKey: ["products", "usage", "years"],
    queryFn: () =>
      axios.get<YearStatus[]>("/products/usage/years", {}).then((result) => result.data),
  });
}
